import React, { useEffect, useRef, useState } from 'react';
import { usePermissions } from '../../../contexts/PermissionsContext';

import {
  addCustomer,
  exportCustomers,
  fetchAreas,
  fetchCategories,
  fetchCustomerGroups,
  fetchCustomers,
  fetchDistricts,
  fetchLogisticLines,
  fetchPaymentMethods,
  importCustomers,
  updateCustomer,
} from '../services/customersService';
import Pagination from '../../../components/Pagination';
import { showSuccessToast, showWarningToast } from '../../../utils/toastHelper';
import Loader from '../../../components/Loader';
import { useTranslation } from 'react-i18next';
import ImportCustomersModal from '../components/ImportCustomersModal';
import AddCustomerModal from '../components/AddCustomerModal';
import EditCustomerModal from '../components/EditCustomerModal';
import ViewCustomerModal from '../components/ViewCustomerModal';
import { printTable } from '../../../utils/printUtils';
import FileNames from '../../../constants/fileNames';

export default function CateringCustomers() {
  const { permissions } = usePermissions();
  const { t } = useTranslation('Customer');

  const [modalData, setModalData] = useState({});

  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [isViewModalOpen, setViewModalOpen] = useState(false);
  const [isImportModalOpen, setImportModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // const [selectAll, setSelectAll] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState(new Set());

  const [loading, setLoading] = useState(false);
  const [importLoading, setimportLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [areas, setAreas] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [logisticLines, setLogisticLines] = useState([]);

  const [customerCategory, setCustomerCategory] = useState({});
  const [customerGroups, setCustomerGroups] = useState([]);
  // const [origins, setOrigins] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    current_page: 1,
    last_page: 1,
    per_page: 20,
  });
  const [filters, setFilters] = useState({
    customerGroupFilter: [],
  });
  const [printLoading, setPrintLoading] = useState(false);

  const handleOpenModal = (modalType, customer) => {
    setModalData(customer);
    if (modalType === 'import') {
      setImportModalOpen(true);
    } else if (modalType === 'edit') {
      setEditModalOpen(true);
    } else if (modalType === 'add') {
      setAddModalOpen(true);
    } else if (modalType === 'view') {
      setViewModalOpen(true);
    }
  };
  const handleCloseModal = (modalType) => {
    if (modalType === 'import') {
      setImportModalOpen(false);
    } else if (modalType === 'add') {
      setAddModalOpen(false);
    } else if (modalType === 'edit') {
      setEditModalOpen(false);
    } else if (modalType === 'view') {
      setViewModalOpen(false);
    }
    setModalData({});
  };
  const handleCustomerSelect = (e, id) => {
    const isChecked = e.target.checked;
    setSelectedCustomers((prevSelected) => {
      const updatedSelection = new Set(prevSelected);
      if (isChecked) {
        updatedSelection.add(id);
      } else {
        updatedSelection.delete(id);
      }
      return updatedSelection;
    });
  };
  const handleSelectAllChange = (e) => {
    const isChecked = e.target.checked;
    if (isChecked) {
      const allCustomerIds = customers.map((customer) => customer.id);
      setSelectedCustomers(new Set(allCustomerIds));
    } else {
      setSelectedCustomers(new Set());
    }
  };
  const isAllSelected =
    customers.length > 0 &&
    customers.every((customer) => selectedCustomers.has(customer.id));

  const fetchData = async (page = 1, ifSearch = 0) => {
    setLoading(true);

    try {
      const per_page = 50;
      const query = {
        page,
        per_page,
        category: 'catering',
        search: searchQuery,
        customerGroups: filters.customerGroupFilter,
      };

      if (ifSearch) {
        const customerResponse = await fetchCustomers(query);
        if (customerResponse.customers) {
          setCustomers(customerResponse.customers);
        }
        if (customerResponse.pagination) {
          setPagination({
            total: customerResponse.pagination.total,
            current_page: customerResponse.pagination.current_page,
            last_page: customerResponse.pagination.last_page,
            per_page: customerResponse.pagination.per_page,
          });
        }
      } else {
        const [
          customerResponse,
          areasResponse,
          districtsResponse,
          paymentMethodsResponse,
          logisticLinesResponse,
          categoriesResponse,
          customerGroupsResponse,
        ] = await Promise.all([
          fetchCustomers(query),
          fetchAreas(),
          fetchDistricts(),
          fetchPaymentMethods(),
          fetchLogisticLines(),
          fetchCategories(),
          fetchCustomerGroups({ per_page: 9999, category: 'catering' }),
        ]);

        if (customerResponse.customers) {
          setCustomers(customerResponse.customers);
        }
        if (areasResponse.success) {
          setAreas(areasResponse.areas);
        }
        if (districtsResponse.success) {
          setDistricts(districtsResponse.districts);
        }
        if (paymentMethodsResponse.success) {
          setPaymentMethods(paymentMethodsResponse.payment_methods);
        }
        if (logisticLinesResponse.success) {
          setLogisticLines(logisticLinesResponse.logistic_lines);
        }
        if (categoriesResponse.success) {
          const cateringCategory = categoriesResponse.customer_categories.find(
            (category) => category?.name?.toUpperCase() === 'CATERING'
          );
          setCustomerCategory(cateringCategory);
        }
        if (customerGroupsResponse.success) {
          setCustomerGroups(customerGroupsResponse.customer_groups);
        }
        if (customerResponse.pagination) {
          setPagination({
            total: customerResponse.pagination.total,
            current_page: customerResponse.pagination.current_page,
            last_page: customerResponse.pagination.last_page,
            per_page: customerResponse.pagination.per_page,
          });
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(pagination.current_page);
  }, [pagination.current_page]);
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchData(1, 1);
    }, 1000);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery, filters]);
  const paginate = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= pagination.last_page) {
      setPagination((prev) => ({
        ...prev,
        current_page: pageNumber,
      }));
    }
  };
  const handleFilterChange = (filterType, value) => {
    console.log('on filter change.....', filterType, value);
    setFilters((prev) => {
      const newFilterState = { ...prev };

      // Handle "ALL" selection separately
      if (value === 'ALL') {
        // Toggle all items for the current filter type (categories, origins, or process locations)
        newFilterState[filterType] =
          newFilterState[filterType].length ===
          filterTypeData[filterType].length
            ? [] // Deselect all
            : filterTypeData[filterType].map((item) => item.id); // Select all
      } else {
        // Handle individual item selection/deselection
        newFilterState[filterType] = newFilterState[filterType].includes(value)
          ? newFilterState[filterType].filter((item) => item !== value) // Deselect the item
          : [...newFilterState[filterType], value]; // Select the item
      }

      return newFilterState;
    });
  };

  // Define the filter data for each type dynamically
  const filterTypeData = {
    customerGroupFilter: customerGroups,
  };
  const handleImportCustomers = async (formData) => {
    setimportLoading(true);
    const response = await importCustomers(formData);
    if (response.success) {
      setimportLoading(false);
      // handleCloseModal("import");
      fetchData();
      return response;
    }
    setimportLoading(false);
    return false;
  };
  const handleAddCustomerSubmit = async (dataToSend) => {
    try {
      const response = await addCustomer(dataToSend);
      if (response.success) {
        // setCustomers((prevCustomers) => prevCustomers.push(response.customer));
        fetchData(pagination.current_page);

        showSuccessToast(response.message);
        handleCloseModal('add');
      }
      return response;
    } catch (error) {
      console.error('Error:', error);
    }
  };
  const handleExportCustomers = async () => {
    const data = {
      search: searchQuery,
      category: 'catering',
      customerGroups: filters.customerGroupFilter,
    };

    if (selectedCustomers.size !== 0) {
      const selectedCustomersIds = Array.from(selectedCustomers);

      data.selectedCustomers = selectedCustomersIds;
    }
    const response = await exportCustomers(
      data,
      FileNames.CATERING_CUSTOMERS_EXPORT
    );
    // if (response.success) {
    //   showSuccessToast(response.message);
    // }
  };
  const handleEditCustomerSubmit = async (dataToSend, id) => {
    try {
      const response = await updateCustomer(dataToSend, id);

      if (response.success) {
        setCustomers((prevCustomers) =>
          prevCustomers.map((customer) =>
            customer.id === id ? response.customer : customer
          )
        );

        showSuccessToast(response.message);

        handleCloseModal('edit');
      }
      return response;
    } catch (error) {
      console.error('Error:', error);
    }
  };
  const handlePrint = () => {
    if (selectedCustomers.size === 0) {
      showWarningToast(t('NOCUSTOMERSELECTED'));
      return;
    }

    const headers = [
      t('CUSTOMERGROUPCODENAME'),
      t('CUSTOMERCODENAMEEMAIL'),
      t('DELIVERYADDRESS'),
      t('CONTACTPERSONINFO'),
      t('DISTRICT'),
      t('LOGISTICLINE'),
      t('PAYMENTMETHOD'),
      t('EARLIESTDELIVERYTIME'),
      t('LATESTDELIVERYTIME'),
    ];

    const selectedCustomersData = customers.filter((customer) =>
      selectedCustomers.has(customer.id)
    );

    const content = selectedCustomersData.map((customer) => [
      `${customer?.customer_group?.code || ''} | ${customer?.customer_group?.group || ''}`,
      `${customer?.name || ''} | ${customer?.code || ''} | ${customer?.email || ''}`,
      customer?.delivery_address || '',
      `${customer?.contact_name || ''} | ${customer?.contact_phone || ''}`,
      customer?.district?.name || '',
      customer?.logistic_line?.name || '',
      customer?.payment_method?.name || '',
      customer?.earliest_delivery_time || '',
      customer?.latest_delivery_time || '',
    ]);

    printTable(headers, content, 'Catering Customers', setPrintLoading, true);
  };
  return (
    <main className="fbz-customer product-page fbz-container-set catering-page">
      <section className="main-section">
        <section className="data on-responsive-block">
          <div className="row">
            <div className="col-sm-6 col-xl-3 col-lg-4">
              <div className="left">
                <h1>{t('CATERINGCUSTOMER')}</h1>
                <div className="input-card">
                  <img src="images/search-icon.svg" alt="" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t('SEARCH')}
                  />
                </div>
              </div>
            </div>
            <div className="col-lg-4 col-xl-4 col-sm-6">
              <div className="right">
                <div className="filter-container">
                  <div className="filter-section w-changes-cs">
                    <label>{t('CUSTOMERGROUPNAME')}</label>
                    <div className="checkbox-group pro-filter">
                      <label className="custom-checkbox">
                        {t('ALL')}
                        <input
                          type="checkbox"
                          checked={
                            filters.customerGroupFilter.length ===
                            customerGroups.length
                          }
                          onChange={() =>
                            handleFilterChange('customerGroupFilter', 'ALL')
                          }
                        />
                        <span className="checkmark"></span>
                      </label>
                      {customerGroups.map((customerGroup) => (
                        <label
                          key={customerGroup.id}
                          className="custom-checkbox"
                        >
                          {customerGroup.group}
                          <input
                            type="checkbox"
                            checked={filters.customerGroupFilter.includes(
                              customerGroup.id
                            )}
                            onChange={() =>
                              handleFilterChange(
                                'customerGroupFilter',
                                customerGroup.id
                              )
                            }
                          />
                          <span className="checkmark"></span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-4 col-xl-5 col-sm-6">
              <div className="right right-change-cs">
                <div className="button-group">
                  {permissions?.CATERING_CUSTOMER_INFO_HOME_PAGE
                    ?.edit_permission === 1 && (
                    <button
                      className="add-btn"
                      // data-bs-toggle="modal"
                      // data-bs-target="#new-customer"
                      onClick={() => handleOpenModal('add', '')}
                    >
                      + {t('ADD')}
                    </button>
                  )}
                  {permissions?.CATERING_CUSTOMER_INFO_HOME_PAGE
                    ?.edit_permission === 1 && (
                    // <button>
                    //   <img src="images/import.svg" alt="" />
                    //   Import
                    // </button>
                    <button onClick={() => handleOpenModal('import', {})}>
                      <img src="images/import.svg" alt="" />
                      {t('IMPORT')}
                    </button>
                  )}
                  <button onClick={handleExportCustomers}>
                    <img src="images/export.svg" alt="" />
                    {t('EXPORT')}
                  </button>
                  <button
                    className={`print ${selectedCustomers.size === 0 ? 'disable-btn' : ''}`}
                    onClick={handlePrint}
                    disabled={selectedCustomers.size === 0 || printLoading}
                  >
                    {printLoading ? (
                      <span>{t('PRINTING')}...</span>
                    ) : (
                      <>
                        <img src="images/print.svg" alt="" />
                        {t('PRINT')}
                      </>
                    )}
                  </button>
                  {/* {permissions?.CATERING_CUSTOMER_INFO_HOME_PAGE
                      ?.edit_permission === 1 && (
                      <button
                        className="add-btn"
                        // data-bs-toggle="modal"
                        // data-bs-target="#new-customer"
                        onClick={() => handleOpenModal('add', '')}
                      >
                        + {t('ADD')}
                      </button>
                    )} */}
                </div>
              </div>
            </div>
            {/* <div className="col-sm-6">
              <div className="left">
                <h1>{t('CATERINGCUSTOMER')}</h1>
                <div className="input-card">
                  <img src="images/search-icon.svg" alt="" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t('SEARCH')}
                  />
                </div>
              </div>
            </div> */}
            {/* <div className="col-sm-6">
              <div className="right">
                <div className="filter-container">
                  <div className="filter-section w-changes-cs">
                    <label>{t('CUSTOMERGROUPNAME')}</label>
                    <div className="checkbox-group pro-filter">
                      <label className="custom-checkbox">
                        {t('ALL')}
                        <input
                          type="checkbox"
                          checked={
                            filters.customerGroupFilter.length ===
                            customerGroups.length
                          }
                          onChange={() =>
                            handleFilterChange('customerGroupFilter', 'ALL')
                          }
                        />
                        <span className="checkmark"></span>
                      </label>
                      {customerGroups.map((customerGroup) => (
                        <label
                          key={customerGroup.id}
                          className="custom-checkbox"
                        >
                          {customerGroup.group}
                          <input
                            type="checkbox"
                            checked={filters.customerGroupFilter.includes(
                              customerGroup.id
                            )}
                            onChange={() =>
                              handleFilterChange(
                                'customerGroupFilter',
                                customerGroup.id
                              )
                            }
                          />
                          <span className="checkmark"></span>
                        </label>
                      ))}
                    </div>
                  </div> */}
            {/* <div className="button-group">
                    {permissions?.CATERING_CUSTOMER_INFO_HOME_PAGE
                      ?.edit_permission === 1 && (
                      <button
                        className="add-btn"
                        onClick={() => handleOpenModal('add', '')}
                      >
                        + {t('ADD')}
                      </button>
                    )}
                    {permissions?.CATERING_CUSTOMER_INFO_HOME_PAGE
                      ?.edit_permission === 1 && (
                      <button onClick={() => handleOpenModal('import', {})}>
                        <img src="images/import.svg" alt="" />
                        {t('IMPORT')}
                      </button>
                    )}
                    <button onClick={handleExportCustomers}>
                      <img src="images/export.svg" alt="" />
                      {t('EXPORT')}
                    </button>
                    <button
                      className={`print ${selectedCustomers.size === 0 ? 'disable-btn' : ''}`}
                      onClick={handlePrint}
                      disabled={selectedCustomers.size === 0 || printLoading}
                    >
                      {printLoading ? (
                        <span>{t('PRINTING')}...</span>
                      ) : (
                        <>
                          <img src="images/print.svg" alt="" />
                          {t('PRINT')}
                        </>
                      )}
                    </button>
                  </div> */}
            {/* </div>
              </div>
            </div> */}
          </div>
        </section>

        <div className="table-outer">
          <table className="table">
            <thead>
              <tr className="t-heading">
                <th className="t-checkbox">
                  <label className="container">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={handleSelectAllChange}
                    />
                    <span className="checkmark"></span>
                  </label>
                </th>
                <th className="category-group-name">
                  <h5>{t('CUSTOMERGROUPCODENAME')}</h5>
                </th>
                <th className="category-code-name">
                  <h5>{t('CUSTOMERCODENAMEEMAIL')}</h5>
                </th>
                {/* <th className="category">
                  <h5>{t("CUSTOMERINFO")}</h5>
                </th> */}
                <th className="location">
                  <h5>{t('DELIVERYADDRESS')}</h5>
                </th>
                <th className="info">
                  <h5>{t('CONTACTPERSONINFO')}</h5>
                </th>

                <th className="latest-time">
                  <h5>{t('DISTRICT')}</h5>
                </th>
                <th className="latest-time">
                  <h5>{t('LOGISTICLINE')}</h5>
                </th>
                <th className="origin">
                  <h5>{t('PAYMENTMETHOD')}</h5>
                </th>
                <th className="origin">
                  <h5>{t('EARLIESTDELIVERYTIME')}</h5>
                </th>
                <th className="origin">
                  <h5>{t('LATESTDELIVERYTIME')}</h5>
                </th>

                <th className="action">
                  {permissions?.CATERING_CUSTOMER_INFO_HOME_PAGE
                    ?.edit_permission === 1 && (
                    <h5 className="pe-3">{t('ACTION')}</h5>
                  )}
                </th>
              </tr>
            </thead>
            {loading ? (
              <tr>
                <td colSpan="100%" className="loader-container mt-5">
                  <Loader />
                </td>
              </tr>
            ) : (
              <>
                <tbody>
                  {customers.length === 0 ? (
                    <tr>
                      <td colSpan="100%" className="text-center py-4">
                        No record available.
                      </td>
                    </tr>
                  ) : (
                    customers.map((customer, index) => (
                      <tr key={index}>
                        <td className="t-checkbox">
                          <label className="container">
                            <input
                              type="checkbox"
                              checked={selectedCustomers.has(customer.id)}
                              onChange={(e) =>
                                handleCustomerSelect(e, customer.id)
                              }
                            />
                            <span className="checkmark"></span>
                          </label>
                        </td>
                        <td className="category-group-name">
                          <h5>
                            <h5> {customer?.customer_group?.code || ''}</h5>
                            <h5> {customer?.customer_group?.group || ''}</h5>
                          </h5>
                        </td>
                        <td className="category-code-name">
                          <h5>
                            <h5> {customer?.name || ''}</h5>
                            <h5> {customer?.code || ''}</h5>
                            <h5> {customer?.email || ''}</h5>
                          </h5>
                        </td>
                        {/* <td className="code">
                        <h5>
                          {customer.name} {customer.code}
                        </h5>
                      </td> */}
                        <td className="location">
                          <h5>{customer?.delivery_address || ''}</h5>
                        </td>
                        <td className="contact-person ">
                          <h5> {customer?.contact_name || ''}</h5>
                          <h5> {customer?.contact_phone || ''}</h5>
                        </td>
                        <td className="latest-time">
                          <h5>{customer?.district?.name}</h5>
                        </td>
                        <td className="latest-time">
                          <h5>{customer?.logistic_line?.name}</h5>
                        </td>
                        <td className="payment-method ">
                          <h5>{customer?.payment_method?.name}</h5>
                        </td>
                        <td className="earliest-delivery-time ">
                          <h5>{customer.earliest_delivery_time || ''}</h5>
                        </td>

                        <td className="latest-delivery-time ">
                          <h5>{customer.latest_delivery_time || ''}</h5>
                        </td>

                        <td className="action">
                          {permissions?.CATERING_CUSTOMER_INFO_HOME_PAGE
                            ?.edit_permission === 1 && (
                            <>
                              <button
                                className="view"
                                data-bs-toggle="modal"
                                data-bs-target="#customer-detail"
                                onClick={() =>
                                  handleOpenModal('view', customer)
                                }
                              >
                                <img src="images/green-view.svg" alt="View" />
                              </button>

                              <button
                                className="edit"
                                onClick={() =>
                                  handleOpenModal('edit', customer)
                                }
                              >
                                <img src="images/green-edit.svg" alt="Edit" />
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </>
            )}
          </table>
        </div>

        <AddCustomerModal
          isOpen={isAddModalOpen}
          onClose={handleCloseModal}
          handleAddCustomerSubmit={handleAddCustomerSubmit}
          areas={areas}
          districts={districts}
          paymentMethods={paymentMethods}
          logisticLines={logisticLines}
          customerGroups={customerGroups}
          type="CATERING"
        />
        <EditCustomerModal
          customer={modalData}
          isOpen={isEditModalOpen}
          onClose={handleCloseModal}
          handleEditCustomerSubmit={handleEditCustomerSubmit}
          areas={areas}
          districts={districts}
          paymentMethods={paymentMethods}
          logisticLines={logisticLines}
          customerGroups={customerGroups}
          // customerCategory={customerCategory}
          type="CATERING"
        />

        <Pagination
          currentPage={pagination.current_page}
          totalPages={pagination.last_page}
          paginate={paginate}
          loading={loading}
        />
        <ImportCustomersModal
          isOpen={isImportModalOpen}
          onClose={handleCloseModal}
          handleImportCustomers={handleImportCustomers}
          importLoading={importLoading}
          customerCategory={customerCategory}
          sample="customers"
        />
        <ViewCustomerModal
          customer={modalData}
          isOpen={isViewModalOpen}
          onClose={handleCloseModal}
        />
      </section>
    </main>
  );
}
